import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { Plus, X } from 'lucide-react';

const LETTER = { 'A+':97,'A':93,'A-':90,'B+':87,'B':83,'B-':80,'C+':77,'C':73,'C-':70,'D+':67,'D':63,'D-':60,'F':50 };

export default function GradeCalc() {
  const [tab, setTab] = useState('grade');
  return (
    <CalcShell look="cnet" title="Grade Calculator" subtitle="Compute weighted grade average and find the final-exam grade you need.">
      <div className="flex gap-2 mb-5">
        {[['grade','Weighted Grade'],['final','Final Grade Needed']].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === v ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>{l}</button>
        ))}
      </div>
      {tab === 'grade' ? <GradeAvg /> : <FinalGrade />}
    </CalcShell>
  );
}

function FinalGrade() {
  const [current, setCurrent] = useState(85);
  const [target, setTarget] = useState(90);
  const [weight, setWeight] = useState(30); // weight of final in %
  const need = ((Number(target) - (100 - Number(weight)) * Number(current) / 100) / Number(weight)) * 100;
  return (
    <>
      <p className="text-sm text-slate-600 mb-4">Given your current grade and the weight of the final exam, find the grade you need on the final to reach a target.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="field"><label>Current Grade (%)</label><input type="number" value={current} onChange={e => setCurrent(e.target.value)}/></div>
        <div className="field"><label>Target Overall Grade (%)</label><input type="number" value={target} onChange={e => setTarget(e.target.value)}/></div>
        <div className="field"><label>Final Exam Weight (%)</label><input type="number" value={weight} onChange={e => setWeight(e.target.value)}/></div>
      </div>
      <div className="result-panel mt-4">
        <div className="result-title">You need on the final</div>
        <div className="result-value">{need.toFixed(2)}%</div>
        {need > 100 && <div className="text-sm text-rose-600 mt-1">Not achievable — target too high for the remaining weight.</div>}
        {need < 0 && <div className="text-sm text-emerald-600 mt-1">You've already achieved your target! 🎉</div>}
      </div>
    </>
  );
}

function GradeAvg() {
  const [rows, setRows] = useState([
    { name: 'Homework 1', grade: '90', weight: 5 },
    { name: 'Project', grade: 'B', weight: 20 },
    { name: 'Midterm exam', grade: '88', weight: 20 }
  ]);
  const [mode, setMode] = useState('percent');
  const [planGoal, setPlanGoal] = useState(90);
  const [planWeight, setPlanWeight] = useState(30);

  const c = useMemo(() => {
    let totalW = 0, weighted = 0;
    rows.forEach(r => {
      const w = +r.weight || 0;
      let g = 0;
      if (mode === 'letter') g = LETTER[r.grade.toUpperCase()] ?? parseFloat(r.grade) ?? 0;
      else g = parseFloat(r.grade) || 0;
      if (!isNaN(g)) { totalW += w; weighted += g * w; }
    });
    return { avg: totalW > 0 ? weighted / totalW : 0, totalW };
  }, [rows, mode]);
  const need = ((Number(planGoal) * 100 - (100 - Number(planWeight)) * c.avg) / Number(planWeight));
  const add = () => setRows([...rows, { name: '', grade: '', weight: 10 }]);
  const upd = (i, k, v) => { const nr = [...rows]; nr[i][k] = v; setRows(nr); };
  return (
    <>
      <div className="flex gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === 'percent'} onChange={() => setMode('percent')} /> Points / Percentage</label>
        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === 'letter'} onChange={() => setMode('letter')} /> Letters</label>
      </div>
      <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600 mb-2 px-1">
        <div className="col-span-6">Assignment</div><div className="col-span-3">Grade</div><div className="col-span-2">Weight %</div><div></div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
          <input value={r.name} onChange={e => upd(i, 'name', e.target.value)} placeholder="Assignment" className="col-span-6 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50" />
          <input value={r.grade} onChange={e => upd(i, 'grade', e.target.value)} placeholder={mode === 'letter' ? 'A/B/C' : '0-100'} className="col-span-3 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50" />
          <input type="number" value={r.weight} onChange={e => upd(i, 'weight', +e.target.value || 0)} className="col-span-2 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50" />
          <button onClick={() => setRows(rows.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={add} className="flex items-center gap-1 text-sm text-blue-600 font-medium"><Plus className="w-4 h-4" /> Add row</button>
      <div className="result-panel mt-6"><div className="result-title">Weighted Grade</div><div className="result-value">{c.avg.toFixed(2)}%</div><div className="text-sm text-slate-600 mt-1">Total weight covered: {c.totalW}%</div></div>

      {/* Final Grade Planning */}
      <div className="mt-6 p-4 rounded-xl bg-slate-50 border">
        <h4 className="font-semibold mb-2">Final Grade Planning (Optional)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="field"><label>Final Grade Goal (%)</label><input type="number" value={planGoal} onChange={e => setPlanGoal(e.target.value)}/></div>
          <div className="field"><label>Weight of Remaining Tasks (%)</label><input type="number" value={planWeight} onChange={e => setPlanWeight(e.target.value)}/></div>
        </div>
        <div className="mt-2 text-sm">You need <b>{need.toFixed(2)}%</b> on the remaining tasks to reach your goal.</div>
      </div>
    </>
  );
}
